import React, { useState, useEffect } from "react";
import { Guest, GuestWithDetails, RsvpResponse } from "../types";
import RsvpHero from "../components/rsvp/RsvpHero";
import GuestSelector from "../components/rsvp/GuestSelector";
import NearbyGuestsStep from "../components/rsvp/NearbyGuestsStep";
import RsvpForm from "../components/rsvp/RsvpForm";
import SuccessMessage from "../components/rsvp/SuccessMessage";

const Home: React.FC = () => {
  // Step 1: Guest selection
  const [allGuests, setAllGuests] = useState<Guest[]>([]);
  const [selectedGuestId, setSelectedGuestId] = useState<string>("");
  const [loadingGuests, setLoadingGuests] = useState(true);

  // Step 2: Selected guest details
  const [selectedGuest, setSelectedGuest] = useState<GuestWithDetails | null>(
    null,
  );
  const [loadingGuest, setLoadingGuest] = useState(false);

  // Step 3: Nearby guests selection
  const [nearbyGuests, setNearbyGuests] = useState<any[]>([]);
  const [selectedNearbyGuests, setSelectedNearbyGuests] = useState<Set<number>>(
    new Set(),
  );
  const [showingNearbyStep, setShowingNearbyStep] = useState(false);

  // Step 4: RSVP form
  const [showingForm, setShowingForm] = useState(false);
  const [responses, setResponses] = useState<Map<number, RsvpResponse>>(
    new Map(),
  );
  const [guestMessages, setGuestMessages] = useState<Map<number, string>>(
    new Map(),
  );

  // Step 5: Submission
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Load all guests on mount
  useEffect(() => {
    const loadAllGuests = async () => {
      try {
        const response = await fetch("/api/guests/search?q=");
        const data = await response.json();
        setAllGuests(
          data.sort((a: Guest, b: Guest) =>
            `${a.firstName} ${a.lastName || ""}`.localeCompare(
              `${b.firstName} ${b.lastName || ""}`,
            ),
          ),
        );
      } catch (err) {
        console.error("Error loading guests:", err);
        setError("Erreur lors du chargement de la liste des invités");
      } finally {
        setLoadingGuests(false);
      }
    };
    loadAllGuests();
  }, []);

  // Handle guest selection from dropdown
  const handleGuestSelect = async (guestId: string) => {
    if (!guestId) {
      // Clear selection
      setSelectedGuestId("");
      setSelectedGuest(null);
      setResponses(new Map());
      setGuestMessages(new Map());
      setNearbyGuests([]);
      setSelectedNearbyGuests(new Set());
      setShowingNearbyStep(false);
      setShowingForm(false);
      setError("");
      return;
    }

    setSelectedGuestId(guestId);
    setLoadingGuest(true);
    setError("");

    try {
      const response = await fetch(`/api/guests/${guestId}`);
      const data: GuestWithDetails = await response.json();
      setSelectedGuest(data);

      // Initialize responses with existing RSVPs
      const newResponses = new Map<number, RsvpResponse>();
      data.invitations.forEach((inv) => {
        const existingRsvp = data.rsvps.find((r) => r.eventId === inv.eventId);
        newResponses.set(inv.eventId, {
          eventId: inv.eventId,
          attending: existingRsvp?.attending ?? null,
        });
      });
      setResponses(newResponses);

      // Initialize message for main guest
      const newMessages = new Map<number, string>();
      if (data.message) {
        newMessages.set(data.id, data.message);
      }
      setGuestMessages(newMessages);

      // Load nearby guests with same event invitations
      const nearbyResponse = await fetch(`/api/guests/${guestId}/nearby`);
      const nearbyData = await nearbyResponse.json();
      setNearbyGuests(nearbyData);

      // If there are nearby guests, show selection step, otherwise go directly to form
      if (nearbyData.length > 0) {
        setShowingNearbyStep(true);
      } else {
        setShowingForm(true);
      }
    } catch (err) {
      setError("Erreur lors du chargement de vos informations");
    } finally {
      setLoadingGuest(false);
    }
  };

  // Update a response for an event
  const handleResponseChange = (eventId: number, attending: boolean) => {
    setError(""); // Clear error when user makes a change
    setResponses((prev) => {
      const newResponses = new Map(prev);
      newResponses.set(eventId, { eventId, attending });
      return newResponses;
    });
  };

  // Toggle nearby guest selection
  const toggleNearbyGuest = (guestId: number) => {
    setSelectedNearbyGuests((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(guestId)) {
        newSet.delete(guestId);
      } else {
        newSet.add(guestId);
      }
      return newSet;
    });
  };

  // Continue to form after selecting nearby guests
  const handleContinueToForm = () => {
    setShowingNearbyStep(false);
    setShowingForm(true);
  };

  // Submit RSVP responses
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGuest) return;

    // Validate that all events have a response
    const allEventsAnswered = selectedGuest.invitations.every((inv) => {
      const response = responses.get(inv.eventId);
      return (
        response &&
        response.attending !== null &&
        response.attending !== undefined
      );
    });

    if (!allEventsAnswered) {
      setError(
        "Merci de répondre à tous les événements avant d'envoyer votre RSVP",
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      const responsesArray = Array.from(responses.values());

      // Prepare all guests for single submission
      const allGuests = [
        {
          guestId: selectedGuest.id,
          responses: responsesArray,
          message: guestMessages.get(selectedGuest.id) || "",
        },
        ...Array.from(selectedNearbyGuests).map((nearbyGuestId) => ({
          guestId: nearbyGuestId,
          responses: responsesArray,
          message: guestMessages.get(nearbyGuestId) || "",
        })),
      ];

      // Submit all guests in a single request
      const response = await fetch("/api/rsvp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          guests: allGuests,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        setError(data.error || "Une erreur est survenue");
        setLoading(false);
        return;
      }

      setSubmitted(true);
    } catch (err) {
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  // Update message for a specific guest
  const handleMessageChange = (guestId: number, message: string) => {
    setGuestMessages((prev) => {
      const newMessages = new Map(prev);
      newMessages.set(guestId, message);
      return newMessages;
    });
  };

  // Reset to initial state
  const handleReset = () => {
    setSelectedGuestId("");
    setSelectedGuest(null);
    setResponses(new Map());
    setGuestMessages(new Map());
    setSubmitted(false);
    setError("");
    setNearbyGuests([]);
    setSelectedNearbyGuests(new Set());
    setShowingNearbyStep(false);
    setShowingForm(false);
  };

  return (
    <div className="modern-rsvp">
      <RsvpHero />

      <div className="rsvp-content">
        {/* Step 1: Select your name (always visible when not on form) */}
        {!showingForm && !submitted && (
          <div className="rsvp-card">
            <GuestSelector
              guests={allGuests}
              selectedGuestId={selectedGuestId}
              onSelect={handleGuestSelect}
              loading={loadingGuests || loadingGuest}
              error={error}
            />
            {showingNearbyStep &&(
              <NearbyGuestsStep
                nearbyGuests={nearbyGuests}
                selectedIds={selectedNearbyGuests}
                onToggle={toggleNearbyGuest}
                onContinue={handleContinueToForm}
              />
            )}
          </div>
        )}

        {/* Step 2: RSVP Form */}
        {showingForm && selectedGuest && !submitted && (
          <RsvpForm
            guests={[
              {
                id: selectedGuest.id,
                name: `${selectedGuest.firstName} ${selectedGuest.lastName || ""}`.trim(),
              },
              ...Array.from(selectedNearbyGuests)
                .map((id) => nearbyGuests.find((g) => g.id === id))
                .filter((g) => g)
                .map((g) => ({
                  id: g.id,
                  name: `${g.firstName} ${g.lastName || ""}`.trim(),
                })),
            ]}
            events={selectedGuest.invitations.map((inv) => inv.event)}
            responses={responses}
            guestMessages={guestMessages}
            onResponseChange={handleResponseChange}
            onMessageChange={handleMessageChange}
            onSubmit={handleSubmit}
            onReset={handleReset}
            loading={loading}
            error={error}
          />
        )}

        {/* Step 3: Success Message */}
        {submitted && <SuccessMessage />}
      </div>
    </div>
  );
};

export default Home;
